import React, { useState, useMemo } from 'react'
import { useFormatMessage } from '@babylon/intl'
import { Pagination, Spinner } from '@babylon/babylon-forms/widgets'
import { PageErrorMessage } from '@babylon/cce-shared-components'
import {
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  Button,
  Radio,
} from '@mui/material'

import PageHeader from '@/components/PageHeader'
import Icon from '@/components/Icon'
import styles from './styles.module.scss'
import messages from './Chats.messages'
import { useGetChats } from './hooks/useGetChats'
import { useClinicianLookup } from './hooks/useClinicianLookup'
import { useReassignChat } from './hooks/useReassignChat'
import ReassignDialog, {
  ReassignDialogState,
} from './components/ReassignDialog'
import ChatIdCell from './components/ChatIdCell'
import PatientNameCell from './components/PatientNameCell'
import ClinicianCell from './components/ClinicianCell'
import { formatDateTime, formatTimeElapsed } from './utils/formatDateTime'
import {
  SearchParams,
  DEFAULT_SEARCH_PARAMS,
  buildChatsFilter,
} from './utils/buildChatsFilter'
import { useColumnSort } from './hooks/useColumnSort'
import { SortByColumn, ChatStatus } from './queries'
import {
  searchButtonSx,
  resetButtonSx,
  reassignButtonSx,
  radioCheckedSx,
  textFieldSx,
  selectSx,
  menuItemSx,
  autocompleteSx,
} from './Chats.styles'

const DEFAULT_LIMIT = 30

const Chats = () => {
  const fm = useFormatMessage()
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedChat, setSelectedChat] = useState<string | null>(null)
  const [
    reassignDialogState,
    setReassignDialogState,
  ] = useState<ReassignDialogState>(ReassignDialogState.CLOSED)
  const [reassignedClinicianName, setReassignedClinicianName] = useState('')
  // searchParams tracks live input values; appliedParams holds the submitted
  // filters so the query only fires on explicit search, not on every keystroke.
  const [searchParams, setSearchParams] = useState<SearchParams>(
    DEFAULT_SEARCH_PARAMS
  )
  const [appliedParams, setAppliedParams] = useState<SearchParams>(
    DEFAULT_SEARCH_PARAMS
  )
  const {
    toggleSort,
    getSortIcon,
    getAriaSort,
    activeSort,
    resetSort,
  } = useColumnSort()

  const handleSortKeyDown = (e: React.KeyboardEvent, column: SortByColumn) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      toggleSort(column)
    }
  }

  const filters = useMemo(
    () =>
      buildChatsFilter(
        appliedParams,
        currentPage,
        DEFAULT_LIMIT,
        activeSort?.direction,
        activeSort?.column
      ),
    [currentPage, appliedParams, activeSort]
  )

  const { data, loading, error } = useGetChats(filters)
  const { reassign, loading: reassigning } = useReassignChat()
  const {
    getClinician,
    clinicianOptions,
    getSelectedClinician,
    loading: consultantsLoading,
    error: consultantsError,
  } = useClinicianLookup()

  const chatsData = data?.getChats?.chats || []
  const totalItems = data?.getChats?.total || 0

  const handleSearch = () => {
    setAppliedParams(searchParams)
    setCurrentPage(1)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    handleSearch()
  }

  const handleReset = () => {
    setSearchParams(DEFAULT_SEARCH_PARAMS)
    setAppliedParams(DEFAULT_SEARCH_PARAMS)
    setSelectedChat(null)
    setCurrentPage(1)
    resetSort()
  }

  const handleSearchParamChange = <K extends keyof SearchParams>(
    param: K,
    value: SearchParams[K]
  ) => {
    setSearchParams((prev) => ({ ...prev, [param]: value }))
  }

  const handleReassignConfirm = async () => {
    if (!selectedChat) return
    setReassignDialogState(ReassignDialogState.LOADING)
    try {
      const result = await reassign([selectedChat])
      const chatResult = result?.chats?.[0]

      if (chatResult?.status === 'NO_AVAILABLE_CLINICIANS') {
        setReassignDialogState(ReassignDialogState.NO_AVAILABLE_CLINICIANS)
        return
      }

      const newClinicianUuid = chatResult?.new_clinician_uuid
      setReassignedClinicianName(
        newClinicianUuid ? getClinician(newClinicianUuid)?.full_name ?? '' : ''
      )
      setSelectedChat(null)
      setReassignDialogState(ReassignDialogState.SUCCESS)
    } catch {
      setReassignDialogState(ReassignDialogState.ERROR)
    }
  }

  if (error || consultantsError) {
    return (
      <div className={styles.pageContainer}>
        <PageHeader>{fm(messages.title)}</PageHeader>
        <PageErrorMessage errorMessage={fm(messages.error_loading_chats)} />
      </div>
    )
  }

  return (
    <div className={styles.pageContainer}>
      <PageHeader>{fm(messages.title)}</PageHeader>

      <form onSubmit={handleSubmit}>
        <div className={styles.filterPanel}>
          <div className={styles.actionButtons}>
            <Button
              variant="contained"
              size="small"
              type="submit"
              onClick={handleSearch}
              sx={searchButtonSx}
            >
              {fm(messages.search_button)}
            </Button>
            <Button
              variant="contained"
              size="small"
              type="button"
              onClick={handleReset}
              sx={resetButtonSx}
            >
              {fm(messages.reset_button)}
            </Button>
            <Button
              variant="contained"
              size="small"
              type="button"
              onClick={() =>
                setReassignDialogState(ReassignDialogState.CONFIRM)
              }
              disabled={selectedChat === null || reassigning}
              sx={reassignButtonSx}
            >
              {fm(messages.reassign_button)}
            </Button>
          </div>
        </div>

        <div className={styles.tableWrapper}>
          {loading || consultantsLoading ? (
            <div className={styles.loadingOverlay}>
              <Spinner />
            </div>
          ) : (
            <table className={styles.dataTable}>
              <thead>
                <tr className={styles.headerRow}>
                  <th className={styles.checkboxColumn} />
                  <th className={styles.headerCell}>
                    {fm(messages.column_chat_id)}
                  </th>
                  <th
                    className={styles.sortableHeader}
                    role="columnheader"
                    aria-sort={getAriaSort(SortByColumn.DATE_CREATED)}
                    tabIndex={0}
                    onClick={() => toggleSort(SortByColumn.DATE_CREATED)}
                    onKeyDown={(e) =>
                      handleSortKeyDown(e, SortByColumn.DATE_CREATED)
                    }
                  >
                    {fm(messages.column_date_created)}
                    <Icon
                      name={getSortIcon(SortByColumn.DATE_CREATED)}
                      className={
                        getSortIcon(SortByColumn.DATE_CREATED) === 'sort'
                          ? styles.sortIcon
                          : `${styles.sortIcon} ${styles.sortIconActive}`
                      }
                    />
                  </th>
                  <th
                    className={styles.sortableHeader}
                    role="columnheader"
                    aria-sort={getAriaSort(
                      SortByColumn.LAST_CLINICIAN_MESSAGE_TIME
                    )}
                    tabIndex={0}
                    onClick={() =>
                      toggleSort(SortByColumn.LAST_CLINICIAN_MESSAGE_TIME)
                    }
                    onKeyDown={(e) =>
                      handleSortKeyDown(
                        e,
                        SortByColumn.LAST_CLINICIAN_MESSAGE_TIME
                      )
                    }
                  >
                    {fm(messages.column_last_clinician_message_time)}
                    <Icon
                      name={getSortIcon(
                        SortByColumn.LAST_CLINICIAN_MESSAGE_TIME
                      )}
                      className={
                        getSortIcon(
                          SortByColumn.LAST_CLINICIAN_MESSAGE_TIME
                        ) === 'sort'
                          ? styles.sortIcon
                          : `${styles.sortIcon} ${styles.sortIconActive}`
                      }
                    />
                  </th>
                  <th className={styles.headerCell}>
                    {fm(messages.column_time_from_last_clinician_message)}
                  </th>
                  <th
                    className={styles.sortableHeader}
                    role="columnheader"
                    aria-sort={getAriaSort(
                      SortByColumn.LAST_PATIENT_MESSAGE_TIME
                    )}
                    tabIndex={0}
                    onClick={() =>
                      toggleSort(SortByColumn.LAST_PATIENT_MESSAGE_TIME)
                    }
                    onKeyDown={(e) =>
                      handleSortKeyDown(
                        e,
                        SortByColumn.LAST_PATIENT_MESSAGE_TIME
                      )
                    }
                  >
                    {fm(messages.column_last_patient_message_time)}
                    <Icon
                      name={getSortIcon(SortByColumn.LAST_PATIENT_MESSAGE_TIME)}
                      className={
                        getSortIcon(SortByColumn.LAST_PATIENT_MESSAGE_TIME) ===
                        'sort'
                          ? styles.sortIcon
                          : `${styles.sortIcon} ${styles.sortIconActive}`
                      }
                    />
                  </th>
                  <th className={styles.headerCell}>
                    {fm(messages.column_patient_first_name)}
                  </th>
                  <th className={styles.headerCell}>
                    {fm(messages.column_patient_last_name)}
                  </th>
                  <th
                    className={`${styles.headerCell} ${styles.clinicianColumn}`}
                  >
                    {fm(messages.column_clinician_name)}
                  </th>
                  <th className={styles.headerCell}>
                    {fm(messages.column_status)}
                  </th>
                </tr>
                <tr className={styles.filterRow}>
                  <th className={styles.checkboxColumn} />
                  <th className={styles.filterCell}>
                    <TextField
                      value={searchParams.chatId}
                      onChange={(e) =>
                        handleSearchParamChange('chatId', e.target.value)
                      }
                      placeholder=""
                      aria-label={fm(messages.column_chat_id)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={textFieldSx}
                    />
                  </th>
                  <th className={styles.filterCell} />
                  <th className={styles.filterCell} />
                  <th className={styles.filterCell} />
                  <th className={styles.filterCell} />
                  <th className={styles.filterCell}>
                    <TextField
                      value={searchParams.patientFirstName}
                      onChange={(e) =>
                        handleSearchParamChange(
                          'patientFirstName',
                          e.target.value
                        )
                      }
                      placeholder=""
                      aria-label={fm(messages.column_patient_first_name)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={textFieldSx}
                    />
                  </th>
                  <th className={styles.filterCell}>
                    <TextField
                      value={searchParams.patientLastName}
                      onChange={(e) =>
                        handleSearchParamChange(
                          'patientLastName',
                          e.target.value
                        )
                      }
                      placeholder=""
                      aria-label={fm(messages.column_patient_last_name)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={textFieldSx}
                    />
                  </th>
                  <th className={styles.filterCell}>
                    <Autocomplete
                      size="small"
                      sx={autocompleteSx}
                      options={clinicianOptions}
                      getOptionLabel={(option) => option.full_name}
                      value={getSelectedClinician(searchParams.clinicianUuid)}
                      onChange={(_event, newValue) => {
                        handleSearchParamChange(
                          'clinicianUuid',
                          newValue?.uuid ?? ''
                        )
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder=""
                          aria-label={fm(messages.column_clinician_name)}
                          variant="outlined"
                          size="small"
                        />
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.uuid === value.uuid
                      }
                      slotProps={{
                        paper: {
                          sx: menuItemSx,
                        },
                      }}
                    />
                  </th>
                  <th className={styles.filterCell}>
                    <Select
                      value={searchParams.status}
                      onChange={(e) =>
                        handleSearchParamChange(
                          'status',
                          e.target.value as ChatStatus | ''
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          e.stopPropagation()
                        }
                      }}
                      displayEmpty
                      inputProps={{ 'aria-label': fm(messages.column_status) }}
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={selectSx}
                    >
                      <MenuItem value="" sx={menuItemSx}>
                        {fm(messages.status_all)}
                      </MenuItem>
                      <MenuItem value={ChatStatus.ACTIVE} sx={menuItemSx}>
                        {fm(messages.status_active)}
                      </MenuItem>
                      <MenuItem value={ChatStatus.CLOSED} sx={menuItemSx}>
                        {fm(messages.status_closed)}
                      </MenuItem>
                    </Select>
                  </th>
                </tr>
              </thead>
              <tbody>
                {chatsData.length === 0 ? (
                  <tr>
                    <td colSpan={10} className={styles.noResults}>
                      {fm(messages.no_results_found)}
                    </td>
                  </tr>
                ) : (
                  chatsData.map((chat) => (
                    <tr
                      key={chat.chat_id}
                      className={`${styles.dataRow}${
                        chat.status === ChatStatus.CLOSED
                          ? ` ${styles.closedRow}`
                          : ''
                      }`}
                      onClick={() => {
                        if (chat.status === ChatStatus.CLOSED) return
                        setSelectedChat(
                          selectedChat === chat.chat_id ? null : chat.chat_id
                        )
                      }}
                    >
                      <td className={styles.checkboxColumn}>
                        <Radio
                          name="chatSelection"
                          size="small"
                          checked={selectedChat === chat.chat_id}
                          onChange={() => setSelectedChat(chat.chat_id)}
                          disabled={chat.status === ChatStatus.CLOSED}
                          sx={radioCheckedSx}
                          onClick={() => {
                            if (selectedChat === chat.chat_id) {
                              setSelectedChat(null)
                            }
                          }}
                        />
                      </td>
                      <td
                        className={`${styles.dataCell} ${styles.chatIdColumn}`}
                      >
                        {chat.chat_id ? (
                          <ChatIdCell chatId={chat.chat_id} />
                        ) : null}
                      </td>
                      <td className={`${styles.dataCell} ${styles.dateCell}`}>
                        {chat.created_at ? formatDateTime(chat.created_at) : ''}
                      </td>
                      <td className={`${styles.dataCell} ${styles.dateCell}`}>
                        {chat.last_clinician_message_time
                          ? formatDateTime(chat.last_clinician_message_time)
                          : ''}
                      </td>
                      <td className={`${styles.dataCell} ${styles.dateCell}`}>
                        {chat.last_clinician_message_time
                          ? formatTimeElapsed(chat.last_clinician_message_time)
                          : ''}
                      </td>
                      <td className={`${styles.dataCell} ${styles.dateCell}`}>
                        {chat.last_patient_message_time
                          ? formatDateTime(chat.last_patient_message_time)
                          : ''}
                      </td>
                      <td className={styles.dataCell}>
                        <PatientNameCell
                          patientUuid={chat.patient_uuid}
                          name={chat.patient_first_name}
                        />
                      </td>
                      <td className={styles.dataCell}>
                        <PatientNameCell
                          patientUuid={chat.patient_uuid}
                          name={chat.patient_last_name}
                        />
                      </td>
                      <td className={styles.dataCell}>
                        <ClinicianCell
                          clinicianUuid={chat.clinician_uuid}
                          getClinician={getClinician}
                        />
                      </td>
                      <td className={styles.dataCell}>
                        {chat.status === ChatStatus.ACTIVE ? (
                          <span
                            className={`${styles.statusPill} ${styles.statusActive}`}
                          >
                            {fm(messages.status_active)}
                          </span>
                        ) : chat.status === ChatStatus.CLOSED ? (
                          <span
                            className={`${styles.statusPill} ${styles.statusClosed}`}
                          >
                            {fm(messages.status_closed)}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </form>

      <div className={styles.paginationBar}>
        <Pagination
          page={currentPage - 1}
          size={DEFAULT_LIMIT}
          totalElements={totalItems}
          maxPages={5}
          onChange={(page: number) => setCurrentPage(page + 1)}
        />
      </div>

      <ReassignDialog
        state={reassignDialogState}
        onClose={() => setReassignDialogState(ReassignDialogState.CLOSED)}
        onConfirm={handleReassignConfirm}
        reassignedClinicianName={reassignedClinicianName}
      />
    </div>
  )
}

export default Chats
